'use client';

import { useEffect, useState } from 'react';
import { EdgeCheckoutMode, resolveEdgeCheckoutMode, sophiManager } from '@benzinga/ads-utils';
import { useIsUserLoggedIn } from './useIsUserLoggedIn';

const SOPHI_LOAD_TIMEOUT_MS = 10000;

export type UseEdgeCheckoutModeResult = {
  checkoutMode: EdgeCheckoutMode;
  isCheckoutModeReady: boolean;
};

const waitForSophiLoad = (): Promise<void> => {
  if (sophiManager.isLoaded()) {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    sophiManager.onLoad(() => resolve());
  });
};

export const useEdgeCheckoutMode = (): UseEdgeCheckoutModeResult => {
  const isLoggedIn = useIsUserLoggedIn();
  const [checkoutMode, setCheckoutMode] = useState<EdgeCheckoutMode>('new_tab');
  const [isCheckoutModeReady, setIsCheckoutModeReady] = useState(false);

  useEffect(() => {
    let isActive = true;
    setIsCheckoutModeReady(false);

    const loadCheckoutMode = async () => {
      await Promise.race([
        waitForSophiLoad(),
        new Promise<void>(resolve => setTimeout(resolve, SOPHI_LOAD_TIMEOUT_MS)),
      ]);

      if (!isActive) return;

      const userType = isLoggedIn ? 'registered' : 'anonymous';
      const decision = await sophiManager.getDecision(userType);

      if (!isActive) return;
      setCheckoutMode(resolveEdgeCheckoutMode(decision?.outcome));
      setIsCheckoutModeReady(true);
    };

    loadCheckoutMode();

    return () => {
      isActive = false;
    };
  }, [isLoggedIn]);

  return { checkoutMode, isCheckoutModeReady };
};
